import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import {
  RiShoppingCart2Line,
  RiAddLine,
  RiSearchLine,
  RiCloseLine,
  RiEditLine,
  RiDeleteBinLine,
  RiCheckLine,
  RiErrorWarningLine,
  RiRefreshLine,
  RiFileTextLine,
  RiStore2Line,
  RiUploadCloud2Line,
  RiFilePdf2Line,
  RiEyeLine,
  RiCalendarLine,
  RiMoneyDollarCircleLine,
  RiHashtag,
} from "react-icons/ri";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const BASE_URL = `${process.env.REACT_APP_API_URL}/purchase`;
const VENDOR_URL = `${process.env.REACT_APP_API_URL}/inventory-vendor`;
const PRODUCT_URL = `${process.env.REACT_APP_API_URL}/product`;

const CLOUDINARY_UPLOAD_PRESET =
  process.env.REACT_APP_UPLOAD_PRESET || "your_upload_preset";
const CLOUDINARY_CLOUD_NAME =
  process.env.REACT_APP_CLOUDINARY_NAME || "your_cloud_name";

const defaultForm = {
  vendorId: "",
  invoiceNumber: "",
  invoiceFile: "",
  products: [],
};

const defaultProduct = {
  productId: "",
  size: "",
  quantity: "",
};

const Spinner = ({ size = "sm" }) => (
  <AiOutlineLoading3Quarters
    className={`animate-spin ${size === "sm" ? "w-4 h-4" : "w-6 h-6"}`}
  />
);

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const formatAmount = (n) =>
  typeof n === "number"
    ? `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
    : "₹0.00";

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, []);
  return (
    <div
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-medium
      ${
        type === "success"
          ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
          : "bg-red-50 border border-red-200 text-red-800"
      }`}
    >
      {type === "success" ? (
        <RiCheckLine className="w-4 h-4 text-emerald-500" />
      ) : (
        <RiErrorWarningLine className="w-4 h-4 text-red-500" />
      )}
      <span>{message}</span>
      <button
        onClick={onClose}
        className="ml-2 opacity-50 hover:opacity-100 transition"
      >
        <RiCloseLine className="w-4 h-4" />
      </button>
    </div>
  );
};

const Modal = ({ title, onClose, children, size = "md" }) =>
  createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative z-10 w-full ${
          size === "lg"
            ? "max-w-4xl"
            : size === "xl"
              ? "max-w-5xl"
              : size === "sm"
                ? "max-w-sm"
                : "max-w-2xl"
        } mx-4 bg-white border border-blue-100 rounded-2xl shadow-2xl overflow-hidden`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-blue-100 bg-blue-50">
          <h2 className="text-base font-semibold text-blue-900 flex items-center gap-2">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="text-blue-400 hover:text-blue-700 transition rounded-lg p-1 hover:bg-blue-100"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 max-h-[80vh] overflow-y-auto bg-white">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );

const InvoiceUploader = ({ value, onChange }) => {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const uploadToCloudinary = async (file) => {
    if (!file) return;

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/jpg",
    ];
    if (!allowedTypes.includes(file.type)) {
      alert("Only PDF, JPG, or PNG files are allowed.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert("File size must be less than 10MB.");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
      formData.append("folder", "invoices");

      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/upload`,
        { method: "POST", body: formData },
      );

      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();
      onChange(data.secure_url);
    } catch (err) {
      alert("Upload failed. Please try again.");
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const handleFile = (e) => uploadToCloudinary(e.target.files[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadToCloudinary(e.dataTransfer.files[0]);
  };

  const isPdf = value && value.toLowerCase().includes(".pdf");

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
        Invoice File
      </label>

      {value ? (
        <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl">
          <div className="w-9 h-9 rounded-lg bg-emerald-100 border border-emerald-200 flex items-center justify-center flex-shrink-0">
            <RiFilePdf2Line className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-emerald-800 truncate">
              Invoice uploaded
            </p>
            <a
              href={value}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-emerald-600 hover:underline flex items-center gap-1"
            >
              <RiEyeLine className="w-3 h-3" /> View file
            </a>
          </div>
          <button
            type="button"
            onClick={() => onChange("")}
            className="text-emerald-400 hover:text-red-500 transition"
            title="Remove"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>
      ) : (
        <div
          onClick={() => !uploading && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`relative flex flex-col items-center justify-center gap-2 py-8 rounded-xl border-2 border-dashed cursor-pointer transition-all
            ${
              dragOver
                ? "border-blue-400 bg-blue-50"
                : "border-blue-200 bg-blue-50/40 hover:border-blue-400 hover:bg-blue-50"
            }
            ${uploading ? "pointer-events-none opacity-70" : ""}`}
        >
          {uploading ? (
            <>
              <Spinner size="lg" />
              <p className="text-sm text-blue-500 font-medium">Uploading…</p>
            </>
          ) : (
            <>
              <div className="w-11 h-11 rounded-xl bg-blue-100 border border-blue-200 flex items-center justify-center">
                <RiUploadCloud2Line className="w-6 h-6 text-blue-500" />
              </div>
              <p className="text-sm font-semibold text-slate-700">
                Drop invoice here or{" "}
                <span className="text-blue-600 underline underline-offset-2">
                  browse
                </span>
              </p>
              <p className="text-xs text-slate-400">PDF, JPG, PNG — max 10MB</p>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleFile}
            className="hidden"
          />
        </div>
      )}
    </div>
  );
};

const ProductRow = ({ item, onChange, onRemove, index, products }) => {
  const selectedProduct = products.find((p) => p._id === item.productId);
  const sizes = selectedProduct?.variations?.map((v) => v.size) || [];

  return (
    <div className="bg-blue-50/30 border border-blue-100 rounded-xl p-4 mb-3">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-blue-600 uppercase tracking-widest">
          Item {index + 1}
        </span>
        <button
          type="button"
          onClick={onRemove}
          className="text-red-400 hover:text-red-600 transition"
        >
          <RiDeleteBinLine className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {/* Product select */}
        <div className="col-span-1">
          <label className="text-[10px] font-semibold text-blue-500 uppercase tracking-widest mb-1 block">
            Product
          </label>
          <select
            value={item.productId}
            onChange={(e) =>
              onChange({ ...item, productId: e.target.value, size: "" })
            }
            className="w-full bg-white border border-blue-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
          >
            <option value="">Select product</option>
            {products.map((p) => (
              <option key={p._id} value={p._id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        {/* Size select */}
        <div className="col-span-1">
          <label className="text-[10px] font-semibold text-blue-500 uppercase tracking-widest mb-1 block">
            Size
          </label>
          <select
            value={item.size}
            onChange={(e) => onChange({ ...item, size: e.target.value })}
            disabled={!item.productId}
            className="w-full bg-white border border-blue-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">Select size</option>
            {sizes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        {/* Quantity */}
        <div className="col-span-1">
          <label className="text-[10px] font-semibold text-blue-500 uppercase tracking-widest mb-1 block">
            Quantity
          </label>
          <input
            type="number"
            min="1"
            placeholder="Qty"
            value={item.quantity}
            onChange={(e) =>
              onChange({ ...item, quantity: parseInt(e.target.value) || "" })
            }
            className="w-full bg-white border border-blue-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
          />
        </div>
      </div>

      {/* Price preview */}
      {item.productId &&
        item.size &&
        (() => {
          const variation = selectedProduct?.variations?.find(
            (v) => v.size === item.size,
          );
          const price = variation?.price || 0;
          const qty = parseInt(item.quantity) || 0;
          return (
            <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
              <span className="text-blue-400">Unit price:</span>
              <span className="font-semibold text-emerald-600">₹{price}</span>
              {qty > 0 && (
                <>
                  <span className="text-slate-300">×</span>
                  <span className="font-semibold text-slate-700">{qty}</span>
                  <span className="text-slate-300">=</span>
                  <span className="font-bold text-slate-800">
                    ₹{(price * qty).toLocaleString("en-IN")}
                  </span>
                </>
              )}
            </div>
          );
        })()}
    </div>
  );
};

const FormBody = ({ form, setForm, vendors, products }) => {
  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleProductChange = (idx, updated) =>
    setForm((f) => {
      const arr = [...f.products];
      arr[idx] = updated;
      return { ...f, products: arr };
    });

  const addProduct = () =>
    setForm((f) => ({
      ...f,
      products: [...f.products, { ...defaultProduct }],
    }));

  const removeProduct = (idx) =>
    setForm((f) => ({
      ...f,
      products: f.products.filter((_, i) => i !== idx),
    }));
  const estimatedTotal = form.products.reduce((sum, item) => {
    const prod = products.find((p) => p._id === item.productId);
    const variation = prod?.variations?.find((v) => v.size === item.size);
    return sum + (variation?.price || 0) * (parseInt(item.quantity) || 0);
  }, 0);

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
            Vendor
          </label>
          <div className="relative">
            <RiStore2Line className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300 pointer-events-none" />
            <select
              name="vendorId"
              value={form.vendorId}
              onChange={handleChange}
              className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition appearance-none cursor-pointer"
            >
              <option value="">Select vendor</option>
              {vendors.map((v) => (
                <option key={v._id} value={v._id}>
                  {v.vendorName}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
            Invoice Number
          </label>
          <div className="relative">
            <RiHashtag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300 pointer-events-none" />
            <input
              type="text"
              name="invoiceNumber"
              value={form.invoiceNumber}
              onChange={handleChange}
              placeholder="e.g. INV-2024-001"
              className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
            />
          </div>
        </div>
      </div>

      <InvoiceUploader
        value={form.invoiceFile}
        onChange={(url) => setForm((f) => ({ ...f, invoiceFile: url }))}
      />

      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
            Products
          </label>
          <button
            type="button"
            onClick={addProduct}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 font-semibold transition"
          >
            <RiAddLine className="w-3.5 h-3.5" /> Add Product
          </button>
        </div>

        {form.products.length === 0 ? (
          <div className="text-center py-8 bg-blue-50/30 border border-dashed border-blue-200 rounded-xl">
            <RiShoppingCart2Line className="w-8 h-8 text-blue-300 mx-auto mb-2" />
            <p className="text-sm text-slate-400">
              No products added. Click "Add Product" to get started.
            </p>
          </div>
        ) : (
          form.products.map((item, idx) => (
            <ProductRow
              key={idx}
              index={idx}
              item={item}
              onChange={(v) => handleProductChange(idx, v)}
              onRemove={() => removeProduct(idx)}
              products={products}
            />
          ))
        )}

        {form.products.length > 0 && (
          <div className="flex items-center justify-end gap-2 mt-2 px-1">
            <span className="text-xs text-slate-400 uppercase tracking-widest font-semibold">
              Estimated Total:
            </span>
            <span className="text-base font-bold text-slate-800">
              {formatAmount(estimatedTotal)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const ViewModal = ({ purchase, onClose }) => (
  <Modal
    title={
      <>
        <RiFileTextLine className="w-5 h-5 text-blue-500" /> Purchase Details
      </>
    }
    onClose={onClose}
    size="lg"
  >
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 gap-4">
        <InfoCard
          icon={<RiStore2Line className="w-4 h-4 text-blue-500" />}
          label="Vendor"
          value={purchase.vendorId?.vendorName || "—"}
        />
        <InfoCard
          icon={<RiHashtag className="w-4 h-4 text-blue-500" />}
          label="Invoice Number"
          value={purchase.invoiceNumber || "—"}
        />
        <InfoCard
          icon={<RiCalendarLine className="w-4 h-4 text-blue-500" />}
          label="Purchase Date"
          value={formatDate(purchase.purchaseDate || purchase.createdAt)}
        />
        <InfoCard
          icon={
            <RiMoneyDollarCircleLine className="w-4 h-4 text-emerald-500" />
          }
          label="Total Amount"
          value={formatAmount(purchase.totalAmount)}
          valueClass="text-emerald-600 font-bold"
        />
      </div>

      {purchase.invoiceFile && (
        <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl">
          <RiFilePdf2Line className="w-5 h-5 text-blue-500 flex-shrink-0" />
          <span className="text-sm font-semibold text-blue-800 flex-1">
            Invoice Document
          </span>
          <a
            href={purchase.invoiceFile}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition"
          >
            <RiEyeLine className="w-3.5 h-3.5" /> View
          </a>
        </div>
      )}

      <div>
        <p className="text-xs font-semibold text-blue-700 uppercase tracking-widest mb-3">
          Products
        </p>
        <div className="border border-blue-100 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-blue-50/50 border-b border-blue-100">
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-blue-500 uppercase tracking-widest">
                  Product
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-blue-500 uppercase tracking-widest">
                  Size
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-blue-500 uppercase tracking-widest">
                  Qty
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-blue-500 uppercase tracking-widest">
                  Unit Price
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-blue-500 uppercase tracking-widest">
                  Subtotal
                </th>
              </tr>
            </thead>
            <tbody>
              {purchase.products?.map((item, i) => {
                const variation = item.productId?.variations?.find(
                  (v) => v.size === item.size,
                );
                const unitPrice = variation?.price || 0;
                return (
                  <tr
                    key={i}
                    className="border-b border-blue-50 last:border-0 hover:bg-blue-50/30"
                  >
                    <td className="px-4 py-3 font-semibold text-slate-800">
                      {item.productId?.name || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-blue-50 border border-blue-100 text-xs font-bold text-blue-700">
                        {item.size}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {item.quantity}
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {unitPrice ? `₹${unitPrice}` : "—"}
                    </td>
                    <td className="px-4 py-3 font-semibold text-emerald-700">
                      {unitPrice
                        ? `₹${(unitPrice * item.quantity).toLocaleString("en-IN")}`
                        : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </Modal>
);

const InfoCard = ({ icon, label, value, valueClass = "text-slate-800" }) => (
  <div className="flex items-start gap-3 px-4 py-3 bg-blue-50/40 border border-blue-100 rounded-xl">
    <div className="mt-0.5">{icon}</div>
    <div>
      <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-widest mb-0.5">
        {label}
      </p>
      <p className={`text-sm font-semibold ${valueClass}`}>{value}</p>
    </div>
  </div>
);

const tableHeaders = [
  { label: "Invoice No.", Icon: RiHashtag },
  { label: "Vendor", Icon: RiStore2Line },
  { label: "Products", Icon: RiShoppingCart2Line },
  { label: "Total Amount", Icon: RiMoneyDollarCircleLine },
  { label: "Date", Icon: RiCalendarLine },
  { label: "Invoice", Icon: RiFilePdf2Line },
  { label: "Actions" },
];

export default function Purchase() {
  const [purchases, setPurchases] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState("");

  const notify = (message, type = "success") => setToast({ message, type });

  const fetchPurchases = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/`);
      setPurchases(res.data?.data || res.data?.purchases || res.data || []);
    } catch {
      notify("Failed to fetch purchases", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await axios.get(`${VENDOR_URL}/`);
      setVendors(res.data?.data || res.data?.vendors || res.data || []);
    } catch {
      console.error("Failed to fetch vendors");
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${PRODUCT_URL}/all`);
      setProducts(res.data?.products || res.data?.data || res.data || []);
    } catch {
      console.error("Failed to fetch products");
    }
  };

  useEffect(() => {
    fetchPurchases();
    fetchVendors();
    fetchProducts();
  }, []);

  const openCreate = () => {
    setForm({ ...defaultForm, products: [{ ...defaultProduct }] });
    setModal("create");
  };

  const openEdit = (purchase) => {
    setSelected(purchase);
    setForm({
      vendorId: purchase.vendorId?._id || purchase.vendorId || "",
      invoiceNumber: purchase.invoiceNumber || "",
      invoiceFile: purchase.invoiceFile || "",
      products:
        purchase.products?.map((p) => ({
          productId: p.productId?._id || p.productId || "",
          size: p.size || "",
          quantity: p.quantity || "",
        })) || [],
    });
    setModal("edit");
  };

  const openView = (purchase) => {
    setSelected(purchase);
    setModal("view");
  };

  const openDelete = (purchase) => {
    setSelected(purchase);
    setModal("delete");
  };

  const closeModal = () => {
    setModal(null);
    setSelected(null);
  };

  const validateForm = () => {
    if (!form.vendorId) {
      notify("Please select a vendor", "error");
      return false;
    }
    if (form.products.length === 0) {
      notify("Please add at least one product", "error");
      return false;
    }
    for (const item of form.products) {
      if (!item.productId || !item.size || !item.quantity) {
        notify("Please fill all product fields", "error");
        return false;
      }
    }
    return true;
  };

  const handleCreate = async () => {
    if (!validateForm()) return;
    setActionLoading(true);
    try {
      await axios.post(`${BASE_URL}/`, form);
      notify("Purchase created successfully");
      fetchPurchases();
      closeModal();
    } catch (error) {
      notify(
        error?.response?.data?.message || "Failed to create purchase",
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;
    setActionLoading(true);
    try {
      await axios.put(`${BASE_URL}/${selected._id}`, form);
      notify("Purchase updated successfully");
      fetchPurchases();
      closeModal();
    } catch (error) {
      notify(
        error?.response?.data?.message || "Failed to update purchase",
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await axios.delete(`${BASE_URL}/${selected._id}`);
      notify("Purchase deleted successfully");
      fetchPurchases();
      closeModal();
    } catch {
      notify("Failed to delete purchase", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const filtered = purchases.filter((p) => {
    const vendor =
      typeof p.vendorId === "object" ? p.vendorId?.vendorName || "" : "";
    const inv = p.invoiceNumber || "";
    return (
      vendor.toLowerCase().includes(search.toLowerCase()) ||
      inv.toLowerCase().includes(search.toLowerCase())
    );
  });

  return (
    <div className="min-h-screen bg-white text-slate-800">
      <header className="bg-white border-b border-blue-100 sticky top-0 z-30 shadow-sm">
        <div className="w-full mx-auto px-6 py-3.5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-200">
              <RiShoppingCart2Line className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-base font-bold text-blue-800 tracking-tight">
                Purchases
              </h1>
              <p className="text-[11px] text-blue-400 tracking-widest uppercase">
                Purchase Management
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search purchases..."
                className="bg-blue-50 border border-blue-200 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-700 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 w-52 transition"
              />
            </div>

            <button
              onClick={fetchPurchases}
              disabled={loading}
              title="Refresh"
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-blue-200 text-blue-400 hover:text-blue-600 hover:bg-blue-50 transition disabled:opacity-50"
            >
              <RiRefreshLine
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
            </button>

            <button
              onClick={openCreate}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-4 py-2 rounded-lg transition-all shadow-md shadow-blue-200"
            >
              <RiAddLine className="w-4 h-4" />
              New Purchase
            </button>
          </div>
        </div>
      </header>

      {/* Table */}
      <div className="w-full mx-auto px-6 pt-6 pb-10">
        <div className="bg-white border border-blue-100 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-5 py-3.5 border-b border-blue-50 bg-blue-50/50 flex items-center justify-between">
            <span className="text-sm font-semibold text-blue-800 flex items-center gap-2">
              <RiShoppingCart2Line className="w-4 h-4 text-blue-500" />
              All Purchases
            </span>
            <span className="text-xs text-blue-400">
              {filtered.length} record{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-blue-100 bg-blue-50/30">
                  {tableHeaders.map(({ label, Icon }) => (
                    <th key={label} className="px-4 py-3 text-left">
                      <span className="flex items-center gap-1.5 text-xs font-semibold text-blue-500 uppercase tracking-widest whitespace-nowrap">
                        {Icon && <Icon className="w-3.5 h-3.5" />}
                        {label}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={tableHeaders.length}
                      className="py-20 text-center"
                    >
                      <div className="flex flex-col items-center gap-3 text-blue-400">
                        <AiOutlineLoading3Quarters className="w-7 h-7 animate-spin text-blue-500" />
                        <span className="text-sm">Loading purchases…</span>
                      </div>
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td
                      colSpan={tableHeaders.length}
                      className="py-20 text-center"
                    >
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
                          <RiShoppingCart2Line className="w-7 h-7 text-blue-300" />
                        </div>
                        <span className="text-sm text-slate-400">
                          {search
                            ? "No purchases match your search."
                            : "No purchases found. Create one to get started."}
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((purchase, i) => (
                    <tr
                      key={purchase._id || i}
                      className="border-b border-blue-50 hover:bg-blue-50/50 transition group"
                    >
                      <td className="px-4 py-3">
                        {purchase.invoiceNumber ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-100 rounded-lg text-xs font-bold text-blue-700">
                            <RiHashtag className="w-3 h-3" />
                            {purchase.invoiceNumber}
                          </span>
                        ) : (
                          <span className="text-slate-300 text-xs">
                            No invoice
                          </span>
                        )}
                      </td>

                      <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">
                        {purchase.vendorId?.vendorName || "—"}
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          {purchase.products?.slice(0, 2).map((item, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-50 border border-slate-100 rounded-lg text-xs text-slate-600 w-fit"
                            >
                              <span className="font-semibold text-slate-700">
                                {item.productId?.name || "Product"}
                              </span>
                              <span className="text-slate-300">|</span>
                              <span className="text-blue-600">{item.size}</span>
                              <span className="text-slate-300">×</span>
                              <span>{item.quantity}</span>
                            </span>
                          ))}
                          {purchase.products?.length > 2 && (
                            <span className="text-xs text-blue-400 font-medium">
                              +{purchase.products.length - 2} more
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 border border-emerald-100 rounded-lg text-xs font-bold text-emerald-700">
                          {formatAmount(purchase.totalAmount)}
                        </span>
                      </td>

                      <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                        {formatDate(
                          purchase.purchaseDate || purchase.createdAt,
                        )}
                      </td>

                      <td className="px-4 py-3">
                        {purchase.invoiceFile ? (
                          <a
                            href={purchase.invoiceFile}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 font-semibold transition"
                          >
                            <RiFilePdf2Line className="w-3.5 h-3.5" /> View
                          </a>
                        ) : (
                          <span className="text-xs text-slate-300">—</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openView(purchase)}
                            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100 font-semibold transition"
                          >
                            <RiEyeLine className="w-3.5 h-3.5" /> View
                          </button>
                          <button
                            onClick={() => openEdit(purchase)}
                            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 font-semibold transition"
                          >
                            <RiEditLine className="w-3.5 h-3.5" /> Edit
                          </button>
                          <button
                            onClick={() => openDelete(purchase)}
                            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-500 hover:bg-red-100 font-semibold transition"
                          >
                            <RiDeleteBinLine className="w-3.5 h-3.5" /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {modal === "create" && (
        <Modal
          title={
            <>
              <RiAddLine className="w-5 h-5 text-blue-500" /> New Purchase
            </>
          }
          onClose={closeModal}
          size="xl"
        >
          <FormBody
            form={form}
            setForm={setForm}
            vendors={vendors}
            products={products}
          />
          <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-blue-50">
            <button
              onClick={closeModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiCheckLine className="w-4 h-4" />
              )}
              Create Purchase
            </button>
          </div>
        </Modal>
      )}

      {modal === "edit" && (
        <Modal
          title={
            <>
              <RiEditLine className="w-5 h-5 text-blue-500" /> Edit Purchase
              {selected?.invoiceNumber ? ` — ${selected.invoiceNumber}` : ""}
            </>
          }
          onClose={closeModal}
          size="xl"
        >
          <FormBody
            form={form}
            setForm={setForm}
            vendors={vendors}
            products={products}
          />
          <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-blue-50">
            <button
              onClick={closeModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleUpdate}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiCheckLine className="w-4 h-4" />
              )}
              Save Changes
            </button>
          </div>
        </Modal>
      )}

      {modal === "delete" && (
        <Modal
          title={
            <>
              <RiDeleteBinLine className="w-5 h-5 text-red-500" /> Confirm
              Deletion
            </>
          }
          onClose={closeModal}
        >
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
              <RiErrorWarningLine className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-slate-700 font-semibold mb-1 text-base">
              Delete this purchase?
            </p>
            {selected?.invoiceNumber && (
              <p className="text-blue-600 font-bold text-sm mb-1">
                {selected.invoiceNumber}
              </p>
            )}
            <p className="text-slate-400 text-sm mb-6">
              This action cannot be undone. Stock quantities will be reversed.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={closeModal}
                className="flex items-center gap-1.5 px-6 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
              >
                <RiCloseLine className="w-4 h-4" /> Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading}
                className="flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-semibold bg-red-500 hover:bg-red-600 text-white transition shadow-md shadow-red-100 disabled:opacity-60"
              >
                {actionLoading ? (
                  <Spinner />
                ) : (
                  <RiDeleteBinLine className="w-4 h-4" />
                )}
                Delete Purchase
              </button>
            </div>
          </div>
        </Modal>
      )}

      {modal === "view" && selected && (
        <ViewModal purchase={selected} onClose={closeModal} />
      )}

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
